import { useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { AreaContext } from "../context/AreaContext";

const initialAreaState = {
  id: null,
  displayModule: false,
  unclaimedResourcesVisible: false,
  storagePacks: [],
  totalQuotas: 0,
  storageAllocation: 0,
  quotas: {},
  error: null,
  loadAttempt: 1,
  loadMaxAttempts: 1
};

// The backing RDS instance auto-pauses after inactivity, so the first request
// after a quiet period can take a while to come back up. Retry with backoff
// instead of failing immediately.
const MAX_LOAD_ATTEMPTS = 7;
const BASE_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const AreaProvider = ({ areaId, children }) => {
  const { authState } = useOktaAuth();
  const [area, setArea] = useState(initialAreaState);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!authState?.accessToken?.accessToken || !areaId) return;

    let cancelled = false;

    const authorizedFetch = async (url) => {
      const res = await fetch(url, {
        headers: {
          Authorization: authState.accessToken.accessToken,
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.error?.message || "Request failed");
      }

      return res.json();
    };

    const loadArea = async () => {
      setArea({
        ...initialAreaState,
        initialized: false,
        loadMaxAttempts: MAX_LOAD_ATTEMPTS
      });

      const params = new URLSearchParams({
        areaId
      });

      const url = `${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}area/context?${params.toString()}`;

      for (let attempt = 0; attempt < MAX_LOAD_ATTEMPTS; attempt++) {
        if (cancelled) return;

        setArea((prev) => ({
          ...prev,
          loadAttempt: attempt + 1,
          loadMaxAttempts: MAX_LOAD_ATTEMPTS
        }));

        try {
          const areaData = await authorizedFetch(url);

          if (cancelled) return;

          const storagePacks = areaData.storagePacks ?? [];
          const storageAllocation = areaData.totalAllocation;

          setArea({
            id: areaId,
            unclaimedResourcesVisible: false,
            storagePacks,
            storageAllocation,
            totalQuotas: areaData.totalQuotas ?? 0,
            initialized: true,
            error: null,
            quotas: {}
          });
          return;
        } catch (err) {
          console.error(`Failed to initialize area context (attempt ${attempt + 1}/${MAX_LOAD_ATTEMPTS})`, err);

          if (cancelled) return;

          const isLastAttempt = attempt === MAX_LOAD_ATTEMPTS - 1;
          if (isLastAttempt) {
            setArea({
              ...initialAreaState,
              initialized: true,
              error: err.message || "Failed to load area"
            });
            return;
          }

          await sleep(Math.min(BASE_RETRY_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS));
        }
      }
    };

    loadArea();

    return () => {
      cancelled = true;
    };
  }, [areaId, authState?.accessToken?.accessToken, retryCount]);

  const retryArea = () => setRetryCount((count) => count + 1);

  return (
    <AreaContext.Provider value={{ area, setArea, retryArea }}>
      {children}
    </AreaContext.Provider>
  );
};
